import type { AdminMoneyHoldDto } from '@carcheck/shared';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { HoldCaptureStory } from './HoldCaptureStory';
import { tokens } from '../../../design';
import { formatCurrency } from '../../../utils/format';
import { orderSearchLink } from '../_lib/moneyHealthLinks';
import { holdDeadlineText } from '../_lib/moneyHealthText';

/**
 * One live hold. The SAME row serves both hold lists — they are one population
 * split by one question, and a reader flipping between them should not have to
 * re-learn a layout.
 *
 * The link is a filtered ORDERS LIST, not a detail route: `/orders/:id` does
 * not exist in this router (AUT-331). When `orderNumber` is `null` — the order
 * row could not be read at all — the row renders «открывать нечего» as plain
 * text and no link. A dead `<Link>` is worse than no link: it typechecks,
 * renders, and dead-ends on the catch-all mid-incident.
 */
const shortId = (id: string): string =>
  id.length <= 12 ? id : `${id.slice(0, 8)}…${id.slice(-4)}`;

/**
 * The row's single affordance. `null` href = the order row is missing, so
 * there is genuinely nothing to open and we say that instead of linking.
 */
const OpenOrderCell = ({ href }: { href: string | null }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box sx={{ ml: 'auto', flexShrink: 0 }}>
      {href === null ? (
        <Typography
          sx={{ fontSize: 11, fontWeight: 500, color: tokens.text.disabled }}
          data-testid="hold-row-no-link"
        >
          {t('moneyHealth.holds.nothingToOpen')}
        </Typography>
      ) : (
        <RouterLink to={href} style={{ textDecoration: 'none' }}>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: 11,
              fontWeight: 600,
              color: tokens.brand.primary,
            }}
          >
            {t('moneyHealth.holds.openOrder')}
            <ChevronRightIcon sx={{ fontSize: 14 }} />
          </Box>
        </RouterLink>
      )}
    </Box>
  );
};

/** «холд» / «списание…» — both are live holds; only one has a request out. */
const HoldStatusChip = ({ status }: { status: AdminMoneyHoldDto['status'] }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      component="span"
      sx={{
        fontSize: 10,
        fontWeight: 700,
        px: 1.5,
        py: 0.25,
        borderRadius: `${tokens.radius.pill}px`,
        background: status === 'capturing' ? tokens.state.infoBg : tokens.surface.muted,
        color: status === 'capturing' ? tokens.state.info : tokens.text.secondary,
      }}
    >
      {t(`moneyHealth.holds.status.${status}`)}
    </Box>
  );
};

export const HoldRow = ({
  hold,
  showPaymentId = false,
  now,
}: {
  hold: AdminMoneyHoldDto;
  /** Escalated rows quote the payment id — it is what an escalation carries. */
  showPaymentId?: boolean;
  now?: number | undefined;
}): ReactElement => {
  const { t } = useTranslation();
  const href = orderSearchLink(hold.orderNumber);
  const deadline = holdDeadlineText(hold.holdExpiresAt, now);

  return (
    <Box
      data-testid="hold-row"
      data-payment-id={hold.paymentId}
      sx={{
        px: 3.5,
        py: 1.5,
        borderBottom: `1px solid ${tokens.border.default}`,
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
        <Typography
          sx={{
            fontFamily: tokens.font.mono,
            fontSize: 13,
            fontWeight: hold.orderNumber === null ? 500 : 700,
            color: hold.orderNumber === null ? tokens.text.disabled : tokens.text.primary,
            flexShrink: 0,
          }}
        >
          {hold.orderNumber === null ? t('moneyHealth.holds.noOrder') : `№ ${hold.orderNumber}`}
        </Typography>

        {showPaymentId ? (
          <Typography
            sx={{ fontFamily: tokens.font.mono, fontSize: 11, color: tokens.text.tertiary }}
          >
            {shortId(hold.paymentId)}
          </Typography>
        ) : null}

        <Typography
          sx={{
            fontFamily: tokens.font.mono,
            fontSize: 13,
            fontWeight: 700,
            color: tokens.text.primary,
          }}
        >
          {formatCurrency(Number(hold.amount), hold.currency)}
        </Typography>

        <HoldStatusChip status={hold.status} />

        <Typography sx={{ fontSize: 11, color: tokens.text.secondary }}>
          {t(deadline.key, { ...deadline.values })}
        </Typography>

        <OpenOrderCell href={href} />
      </Box>

      <HoldCaptureStory capture={hold.capture} now={now} />
    </Box>
  );
};
